import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WaiterLayout from '../../components/WaiterLayout';
import { restaurantOrderService } from '../../services/restaurantOrderService';
import { RestaurantOrderListDTO } from '../../types';
import { useCurrencyFormat } from '../../hooks/useCurrency';
import { RefreshCw, Plus, Receipt } from 'lucide-react';

const STATUS_COLORS: Record<string, string> = {
  Open: '#f59e0b',
  Held: '#6b7280',
  Preparing: '#3b82f6',
  Ready: '#8b5cf6',
  Completed: '#10b981',
  Voided: '#ef4444',
};

const WaiterMyOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const fmt = useCurrencyFormat();
  const [orders, setOrders] = useState<RestaurantOrderListDTO[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Server auto-scopes this list to orders created by the current waiter
      // (no Orders.ViewAll permission) — no client-side filtering needed.
      const res = await restaurantOrderService.getAll(1, 100);
      const data = (res?.data as any)?.data?.data ?? (res?.data as any)?.data ?? [];
      setOrders(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <WaiterLayout>
      <div style={{ padding: '20px', maxWidth: 900, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>My Orders</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={load}
              disabled={loading}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}
            >
              <RefreshCw size={14} className={loading ? 'refresh-icon-spinning' : ''} /> Refresh
            </button>
            <button
              onClick={() => navigate('/waiter/new-order')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            >
              <Plus size={14} /> New Order
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Loading…</div>
        ) : orders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
            <Receipt size={40} strokeWidth={1} style={{ marginBottom: 8 }} />
            <div>You haven't placed any orders yet.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {orders.map((o) => (
              <div
                key={o.id}
                onClick={() => navigate('/waiter/new-order')}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '14px 16px', borderRadius: 10, border: '1px solid #e5e7eb',
                  background: '#fff', cursor: 'pointer',
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    {o.orderNumber}
                    {o.tableNumber && <span style={{ color: '#6b7280', fontWeight: 500 }}> · Table {o.tableNumber}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                    {o.lineCount} item{o.lineCount === 1 ? '' : 's'} · {new Date(o.createdAt).toLocaleTimeString()}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <span style={{ fontWeight: 700 }}>{fmt(o.totalAmount)}</span>
                  <span
                    style={{
                      fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999,
                      color: '#fff', background: STATUS_COLORS[o.status] ?? '#6b7280',
                    }}
                  >
                    {o.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </WaiterLayout>
  );
};

export default WaiterMyOrdersPage;
